'use strict';
/* ==========================================================================
   core-local/rockcycle.js — an honest rock-cycle graph (DESIGN.md §4.2). Pure: no DOM, no engine globals.
   Works in node (module.exports: tools/rockcheck.js, tools/contentcheck.js) and in the page (window.RockCycle).
   Registers itself with the question bank as `rocks` when core/bank.js is loaded first.

   A grain's STATE is { kind, place, comp? }:
     kind   magma · igneous: granite, rhyolite, basalt, gabbro · sediment: sand, mud, shells ·
            sedimentary: sandstone, shale, limestone · metamorphic: quartzite, slate, marble, gneiss
     place  'surface' | 'deep'
     comp   magma only: 'granitic' (melted continental rock) | 'basaltic' (mantle / melted ocean-floor rock)
   Igneous rocks, honestly (DESIGN.md §4.2, designer correction 2026-09-27): cooling SPEED sets the crystal size,
   the magma's MAKE-UP sets the rock. Slow underground: granitic → granite, basaltic → gabbro (big crystals).
   Fast at the surface: granitic → rhyolite, basaltic → basalt (tiny crystals). Never "fast = basalt, slow = granite".

   Moves (the cards): weather, deposit, cement, squeeze, melt, cool, uplift, bury, erupt.
     legal(state)          → [{ move, ok, to?, reason?, time }]   every card with its legality (+ a one-line reason)
     apply(state, move)    → the new state (throws on an illegal move)
     solve(from, to)       → { par, path: [move…], states: [state…] } fewest moves (BFS), or null if unreachable
     textbookSolve(from,to)→ the same, restricted to the textbook loop igneous → sediment → sedimentary → metamorphic → magma → igneous
     breaksOrder(from, to) → true when the fastest route is SHORTER than any textbook-loop route (or no textbook route exists)
     timeOf(path)          → { lo, hi, label } years, summed orders of magnitude (never a precise number)
   Every transition carries `src`: the facts-ground.md row it rests on, or null when facts-ground.md doesn't cover it
   (tools/rockcheck.js lists those for the designer).
   ========================================================================== */
(function (root) {
  const FAMILY = {
    magma: 'magma',
    granite: 'igneous', rhyolite: 'igneous', basalt: 'igneous', gabbro: 'igneous',
    sand: 'sediment', mud: 'sediment', shells: 'sediment',
    sandstone: 'sedimentary', shale: 'sedimentary', limestone: 'sedimentary',
    quartzite: 'metamorphic', slate: 'metamorphic', marble: 'metamorphic', gneiss: 'metamorphic',
  };
  const KINDS = Object.keys(FAMILY);
  const PLACES = ['surface', 'deep'];
  const COMPS = ['granitic', 'basaltic'];
  const MOVES = ['weather', 'deposit', 'cement', 'squeeze', 'melt', 'cool', 'uplift', 'bury', 'erupt'];
  const LABEL = { weather: 'Weather', deposit: 'Deposit', cement: 'Cement', squeeze: 'Squeeze', melt: 'Melt', cool: 'Cool', uplift: 'Uplift', bury: 'Bury', erupt: 'Erupt' };
  const isSolid = k => ['igneous', 'sedimentary', 'metamorphic'].includes(FAMILY[k]);

  /* ---------------- durations: orders of magnitude only (facts-ground.md §10) ---------------- */
  const HOUR = 1 / 8766;                                            // a year has 8,766 hours
  const DUR = {
    lava:   { lo: HOUR, hi: 50, label: 'hours to decades', src: 'facts §10 "Lava cools" (Earle: seconds to years; HVO: thick flows years to decades)' },
    pluton: { lo: 100, hi: 1e7, label: 'centuries to millions of years', src: 'facts §10 "Magma underground cools" (Earle: centuries to millions of years)' },
    slow:   { lo: 1e6, hi: 5e7, label: 'millions to tens of millions of years', src: 'facts §10 "Sediment → rock, metamorphism, mountains" (Earle: ~20 Myr a step, order of magnitude)' },
    cement: { lo: 1e6, hi: 5e7, label: 'millions of years (it varies a lot)', src: 'facts §10 (no authoritative lithification figure: DESIGN.md §4.2 wording)' },
    erupt:  { lo: 0, hi: 0, label: 'fast, in deep time (not timed)', src: null },   // facts-ground.md has no figure for magma rising: adds nothing to the clock
  };

  /* ---------------- the transitions: each cites its facts-ground.md row (§11 = the 2026-09-27 model check) ----------------
     `src` is the audit trail tools/rockcheck.js lists (never shown to the student); src null = a DECISION (WARN).
     `rule: true` = a GAME RULE (a model simplification §11 accepts as a rule, not as a fact): no text shown to the
     student may claim it. */
  // weather (surface, solid rock → sediment), facts §11 A1 (Earle §5.2, §5.3 Table 5.1):
  //   sandstone, quartzite → sand (quartz stays as sand grains)                                      §11 A1 VERIFIED
  //   granite, gneiss → sand OR mud (feldspar → clay, quartz stays as sand): the player picks a grain §11 A1 CORRECTED
  //   basalt, gabbro, shale, slate → mud; rhyolite → mud (clay + fine quartz, a fair simplification)  §11 A1 VERIFIED
  //   limestone, marble → dissolve; the dissolved lime ends up as new shells                           §11 A1 + A7 VERIFIED
  const WEATHER = {
    granite: ['sand', 'mud'], gneiss: ['sand', 'mud'],
    sandstone: ['sand'], quartzite: ['sand'],
    basalt: ['mud'], gabbro: ['mud'], rhyolite: ['mud'], shale: ['mud'], slate: ['mud'],
    limestone: ['shells'], marble: ['shells'],
  };
  const WEATHER_SRC = {
    granite: 'facts §11 A1 (Earle §5.3: granite leaves quartz sand AND clay)', gneiss: 'facts §11 A1 (same minerals as granite: sand and clay)',
    sandstone: 'facts §11 A1 (quartz stays as sand grains)', quartzite: 'facts §11 A1 (quartz stays as sand grains)',
    basalt: 'facts §11 A1 (no quartz: weathers to clay)', gabbro: 'facts §11 A1 (no quartz: weathers to clay)',
    rhyolite: 'facts §11 A1 (clay + fine quartz: mud is a fair simplification)', shale: 'facts §11 A1 (already clay-sized)', slate: 'facts §11 A1 (already clay-sized)',
    limestone: 'facts §11 A1 + A7 (calcite dissolves; the ions become new shells) + §6 sinkholes', marble: 'facts §11 A1 + A7 (marble is recrystallized calcite: it dissolves)',
  };
  const GRAIN = { sand: 'the quartz grains that survive', mud: 'clay from the feldspar', shells: 'the dissolved lime' };   // student-facing (§11 A1)
  // cement (deep sediment → sedimentary): facts §10 "Processes" ("compressed and cemented into sedimentary rock");
  // sand → sandstone, mud → shale (§10 Table 7.1 parent names), shells → limestone (§11 A7 + §6 Florida bedrock).
  const CEMENT = { sand: 'sandstone', mud: 'shale', shells: 'limestone' };
  const CEMENT_SRC = { sand: 'facts §10 "Processes" + Table 7.1 (sandstone)', mud: 'facts §10 "Processes" + Table 7.1 (shale/mudrock)', shells: 'facts §11 A7 + §6 (shells → limestone)' };
  // squeeze (deep, solid rock → metamorphic): §11 A5 (Earle §7.2 Table 7.1) + rhyolite → gneiss at high grade (§11 A4, summary #8).
  const SQUEEZE = { sandstone: 'quartzite', shale: 'slate', limestone: 'marble', granite: 'gneiss', slate: 'gneiss', rhyolite: 'gneiss' };
  const SQUEEZE_SRC = {
    sandstone: 'facts §11 A5 (Table 7.1: sandstone → quartzite)', shale: 'facts §11 A5 (Table 7.1: mudrock → slate)', limestone: 'facts §11 A5 (Table 7.1: limestone → marble)',
    granite: 'facts §11 A5 (Table 7.1: granite → granite gneiss at high grade)', slate: 'facts §11 A5 (slate → phyllite → schist → gneiss above 550 °C)',
    rhyolite: 'facts §11 A4 + summary #8 (rhyolite → gneiss at high grade is real)',
  };
  const SQUEEZE_NO = {                                   // §11 A4 (basalt → amphibolite etc.: rocks the game lacks); A5 end products
    basalt: 'Basalt changes too, but into rocks this game doesn\'t have.',
    gabbro: 'Gabbro changes too, but into rocks this game doesn\'t have.',
    quartzite: 'Squeezed as far as this game goes. More heat would melt it.',
    marble: 'Squeezed as far as this game goes. More heat would melt it.',
    gneiss: 'Squeezed as far as this game goes. More heat would melt it.',
  };
  // melt (deep, solid rock → magma):
  //   granite, rhyolite, gneiss, shale, slate → granitic: §11 A2 VERIFIED (partial melting gives silica-rich magma;
  //     melted mudrock → granite, e.g. the Himalayan leucogranites)
  //   sandstone, quartzite → granitic: §11 A2 "CORRECTED (soft)": a GAME RULE (quartz-rich rock is hard to melt)
  //   basalt, gabbro → basaltic: §11 A2 + summary #3: a GAME RULE. Real basaltic magma comes from partly melting the
  //     MANTLE; the grain stands for deep, dark ocean-floor and mantle rock. No student-facing text may say "melted basalt
  //     makes basalt magma": the game's wording is MAGMA_FROM.
  //   limestone, marble: never (§11 A3: they don't simply melt into ordinary magma).
  const MELT = {
    granite: 'granitic', rhyolite: 'granitic', gneiss: 'granitic', sandstone: 'granitic', quartzite: 'granitic', shale: 'granitic', slate: 'granitic',
    basalt: 'basaltic', gabbro: 'basaltic',
  };
  const MELT_RULE = { sandstone: true, quartzite: true, basalt: true, gabbro: true };
  const MELT_SRC = k => (MELT[k] === 'basaltic' ? 'facts §11 A2 + summary #3: GAME RULE (basaltic magma really comes from the mantle)'
    : MELT_RULE[k] ? 'facts §11 A2: GAME RULE (quartz-rich rock is hard to melt; only its minor feldspar/clay melts)'
    : 'facts §11 A2 (melted continental rock → granitic magma)');
  const MAGMA_FROM = { granitic: 'melted continental rock', basaltic: 'deep ocean-floor and mantle rock' };   // student-facing (§11 summary #3)
  const MELT_NO = {                                      // §11 A3
    limestone: 'Limestone doesn\'t simply melt into magma. Squeeze it or lift it instead.',
    marble: 'Marble doesn\'t simply melt into magma. Lift it up and let the rain dissolve it.',
  };
  // cool: composition sets the rock, speed (place) sets the crystal size. §11 A6 (Earle §3.3, §3.1).
  const COOL = {
    deep: { granitic: 'granite', basaltic: 'gabbro' },       // slow, big crystals
    surface: { granitic: 'rhyolite', basaltic: 'basalt' },   // fast, tiny crystals
  };
  const CRYSTALS = { deep: 'big crystals (cooled slowly)', surface: 'tiny crystals (cooled fast)' };

  /* ---------------- states ---------------- */
  function state(kind, place, comp) {
    if (!FAMILY[kind]) throw new Error('rockcycle: unknown kind ' + kind);
    if (!PLACES.includes(place)) throw new Error('rockcycle: place must be surface or deep');
    if (kind === 'magma') { if (!COMPS.includes(comp)) throw new Error('rockcycle: magma needs comp granitic | basaltic'); return { kind, place, comp }; }
    return { kind, place };
  }
  const key = s => s.kind + (s.kind === 'magma' ? ':' + s.comp : '') + '@' + s.place;
  const parse = k => { const [a, place] = k.split('@'), [kind, comp] = a.split(':'); return state(kind, place, comp); };
  const same = (a, b) => key(a) === key(b);
  const family = k => FAMILY[typeof k === 'string' ? k : k.kind];
  /* where a mission's rock starts when the item doesn't say: solid rock and sediment at the surface, magma deep */
  const defaultPlace = kind => (kind === 'magma' ? 'deep' : 'surface');

  /* ---------------- the moves ---------------- */
  /* a move id is a card ('weather') or a card + the outcome picked ('weather:mud', when a rock has more than one) */
  const baseOf = m => String(m).split(':')[0];
  const variantOf = m => String(m).split(':')[1] || '';
  const labelOf = m => (LABEL[baseOf(m)] || baseOf(m)) + (variantOf(m) ? ' (' + variantOf(m) + ')' : '');
  const cap = x => x.charAt(0).toUpperCase() + x.slice(1);
  const R = (move, ok, extra) => ({ move, label: labelOf(move), ok, ...extra });
  function tryMove(s, move) {
    const k = s.kind, f = FAMILY[k], deep = s.place === 'deep', pick = variantOf(move), base = baseOf(move);
    if (pick && base !== 'weather') throw new Error('rockcycle: unknown move ' + move);
    switch (base) {
      case 'weather': {
        if (deep) return R(move, false, { reason: 'Down here nothing weathers. Rain and wind only work at the surface.' });
        if (k === 'magma') return R(move, false, { reason: 'This is lava. Let it cool into rock first.' });
        if (f === 'sediment') return R(move, false, { reason: 'It\'s already broken into loose bits.' });
        const prods = WEATHER[k];
        if (pick && !prods.includes(pick)) return R(move, false, { reason: `${cap(k)} weathers into ${prods.join(' or ')}, not ${pick}.` });
        const one = p => R(pick || prods.length > 1 ? 'weather:' + p : 'weather', true, { to: state(p, 'surface'), grain: GRAIN[p], dur: DUR.slow, src: WEATHER_SRC[k] });
        if (pick) return one(pick);
        if (prods.length > 1) return { ...one(prods[0]), move: 'weather', label: LABEL.weather, choices: prods.map(one) };   // 'weather' alone = the first
        return one(prods[0]);
      }
      case 'deposit':
        if (f !== 'sediment') return R(move, false, { reason: 'Only loose sediment piles up in layers.' });
        if (deep) return R(move, false, { reason: 'It\'s already buried.' });
        return R(move, true, { to: state(k, 'deep'), dur: DUR.slow, src: 'facts §10 "Processes" (erosion, transport, deposition, burial); "Sediment piles up slowly"' });
      case 'cement':
        if (f !== 'sediment') return R(move, false, { reason: 'Only loose sediment gets cemented into rock.' });
        if (!deep) return R(move, false, { reason: 'It needs burying first: sediment turns to rock hundreds of meters down.' });
        return R(move, true, { to: state(CEMENT[k], 'deep'), dur: DUR.cement, src: CEMENT_SRC[k] + ' + §10 "Sediment → rock (lithification)"' });
      case 'squeeze':
        if (!deep) return R(move, false, { reason: 'Squeezing and heating happen deep underground. Bury it first.' });
        if (k === 'magma') return R(move, false, { reason: 'Magma is melted. Cool it into rock first.' });
        if (f === 'sediment') return R(move, false, { reason: 'Cement the loose bits into rock first.' });
        if (!SQUEEZE[k]) return R(move, false, { reason: SQUEEZE_NO[k] });
        return R(move, true, { to: state(SQUEEZE[k], 'deep'), dur: DUR.slow, src: SQUEEZE_SRC[k] });
      case 'melt':
        if (!deep) return R(move, false, { reason: 'Rock melts deep underground, not at the surface.' });
        if (k === 'magma') return R(move, false, { reason: 'It\'s already melted.' });
        if (f === 'sediment') return R(move, false, { reason: 'Cement the loose bits into rock first.' });
        if (!MELT[k]) return R(move, false, { reason: MELT_NO[k] });
        return R(move, true, { to: state('magma', 'deep', MELT[k]), dur: DUR.slow, src: MELT_SRC(k), rule: !!MELT_RULE[k] });
      case 'cool': {
        if (k !== 'magma') return R(move, false, { reason: 'Only melted rock can cool into new rock.' });
        const to = state(COOL[s.place][s.comp], s.place);
        return R(move, true, { to, crystals: CRYSTALS[s.place], dur: deep ? DUR.pluton : DUR.lava, src: 'facts §11 A6 + ' + (deep ? '§10 "Magma underground cools"' : '§10 "Lava cools"') });
      }
      case 'uplift':
        if (!deep) return R(move, false, { reason: 'It\'s already at the surface.' });
        if (k === 'magma') return R(move, false, { reason: 'Magma doesn\'t get lifted. It erupts.' });
        return R(move, true, { to: state(k, 'surface'), dur: DUR.slow, src: 'facts §11 A8 (uplift) + §10 "Processes", "Sediment → rock, metamorphism, mountains"' });
      case 'bury':
        if (deep) return R(move, false, { reason: 'It\'s already deep.' });
        if (k === 'magma') return R(move, false, { reason: 'This is lava. Let it cool first.' });
        if (f === 'sediment') return R(move, false, { reason: 'Loose sediment gets buried by piling up: play Deposit.' });
        return R(move, true, { to: state(k, 'deep'), dur: DUR.slow, src: 'facts §11 A8 + §10 "Any rock can become any other kind" (reburial)' });
      case 'erupt':
        if (k !== 'magma') return R(move, false, { reason: 'Only magma erupts.' });
        if (!deep) return R(move, false, { reason: 'It\'s already out: this is lava.' });
        return R(move, true, { to: state('magma', 'surface', s.comp), dur: DUR.erupt, src: null });   // no facts row: rockcheck WARNs
      default: throw new Error('rockcycle: unknown move ' + move);
    }
  }
  const legal = s => MOVES.map(m => { const r = tryMove(s, m); return { ...r, time: r.dur ? r.dur.label : null }; });
  function apply(s, move) { const r = tryMove(s, move); if (!r.ok) throw new Error(`rockcycle: can't ${move} ${key(s)}: ${r.reason}`); return r.to; }

  /* ---------------- all states + edges ---------------- */
  const STATES = [];
  for (const k of KINDS) for (const p of PLACES) { if (k === 'magma') COMPS.forEach(c => STATES.push(state(k, p, c))); else STATES.push(state(k, p)); }
  /* every outcome of every card (a card with choices gives one edge per outcome: 'weather:sand', 'weather:mud') */
  const outcomes = s => MOVES.map(m => tryMove(s, m)).filter(r => r.ok).flatMap(r => r.choices || [r]);
  function edges(s) { return outcomes(s).map(r => ({ move: r.move, to: r.to, dur: r.dur })); }
  /* every legal transition in the graph: [{from, move, to, src, rule}] */
  const transitions = () => STATES.flatMap(s => outcomes(s).map(r => ({ from: s, move: r.move, to: r.to, src: r.src || null, rule: !!r.rule, crystals: r.crystals || null })));

  /* the textbook loop: igneous → (weather) sediment → (deposit, cement) sedimentary → (squeeze) metamorphic → (melt) magma → (cool, erupt) igneous;
     place moves (bury / uplift / deposit / erupt) keep the family and are always allowed */
  const TEXTBOOK = new Set(['igneous>sediment', 'sediment>sediment', 'sediment>sedimentary', 'sedimentary>sedimentary', 'sedimentary>metamorphic',
    'metamorphic>metamorphic', 'metamorphic>magma', 'magma>magma', 'magma>igneous', 'igneous>igneous']);
  const isTextbook = (a, b) => TEXTBOOK.has(FAMILY[a.kind] + '>' + FAMILY[b.kind]);

  /* a target: a kind name (any place; for magma any composition) or a full state */
  const reached = (s, to) => (typeof to === 'string' ? s.kind === to : same(s, to));
  const asState = (x, place, comp) => (typeof x === 'string' ? state(x, place || defaultPlace(x), x === 'magma' ? comp || 'granitic' : undefined) : x);
  function bfs(from, to, onlyTextbook) {
    const start = asState(from);
    if (reached(start, to)) return { par: 0, path: [], states: [start] };
    const prev = new Map([[key(start), null]]), q = [start];
    while (q.length) {
      const s = q.shift();
      for (const e of edges(s)) {
        if (onlyTextbook && !isTextbook(s, e.to)) continue;
        const k = key(e.to);
        if (prev.has(k)) continue;
        prev.set(k, { from: s, move: e.move });
        if (reached(e.to, to)) {
          const path = [], states = [e.to];
          for (let c = e.to, p = prev.get(key(c)); p; c = p.from, p = prev.get(key(c))) { path.unshift(p.move); states.unshift(p.from); }
          return { par: path.length, path, states };
        }
        q.push(e.to);
      }
    }
    return null;
  }
  const solve = (from, to) => bfs(from, to, false);
  const textbookSolve = (from, to) => bfs(from, to, true);
  function breaksOrder(from, to) {
    const a = solve(from, to); if (!a) return false;
    const b = textbookSolve(from, to);
    return !b || b.par > a.par;
  }
  const par = (from, to) => { const r = solve(from, to); return r ? r.par : -1; };
  /* replay a list of moves from a state → the states (throws on an illegal move) */
  function run(from, moves) { let s = asState(from); const out = [s]; for (const m of moves) { s = apply(s, m); out.push(s); } return out; }

  /* ---------------- the deep-time clock (orders of magnitude) ---------------- */
  function magnitude(years) {
    if (!(years > 0)) return 'no time at all';
    if (years < 1 / 365) return 'hours';
    if (years < 1) return 'days to months';
    if (years < 10) return 'years';
    if (years < 100) return 'decades';
    if (years < 1000) return 'centuries';
    if (years < 1e6) return 'thousands of years';
    if (years < 1e7) return 'millions of years';
    if (years < 1e8) return 'tens of millions of years';
    if (years < 1e9) return 'hundreds of millions of years';
    return 'billions of years';
  }
  /* the time of a path (moves from a start): each move's range, summed; the label is the order of magnitude of each end */
  function timeOf(from, moves) {
    let s = asState(from), lo = 0, hi = 0;
    for (const m of moves) { const r = tryMove(s, m); if (!r.ok) throw new Error(`rockcycle: can't ${m} ${key(s)}`); lo += r.dur.lo; hi += r.dur.hi; s = r.to; }
    const a = magnitude(lo), b = magnitude(hi);
    return { lo, hi, label: a === b ? a : `${a} to ${b}` };
  }

  const RockCycle = {
    FAMILY, KINDS, PLACES, COMPS, MOVES, LABEL, DUR, WEATHER, CEMENT, SQUEEZE, MELT, COOL, CRYSTALS, STATES, TEXTBOOK,
    GRAIN, MAGMA_FROM, MELT_RULE, baseOf, variantOf, labelOf, outcomes,
    state, key, parse, same, family, isSolid, defaultPlace, asState,
    tryMove, legal, apply, edges, transitions, isTextbook,
    solve, textbookSolve, breaksOrder, par, run, magnitude, timeOf,
  };
  if (typeof module !== 'undefined' && module.exports) module.exports = RockCycle;
  root.RockCycle = RockCycle;
  if (root.Bank && typeof root.Bank.register === 'function') root.Bank.register('rocks', RockCycle);
})(typeof window !== 'undefined' ? window : globalThis);
